import React, { useState, useContext, useEffect, useRef } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Mic, Square, Loader2, CheckCircle2, AlertCircle, Zap, ShoppingCart, Edit2, X, Check, AlertTriangle, Package, ChevronDown, ChevronUp, Trash2, Plus, ExternalLink } from 'lucide-react';
import { showToast } from '@/lib/custom-toast';
import { toast } from 'sonner';
import { formatCurrencyInput, parseCurrency } from '@/utils/currencyFormat';

const VoiceRecorderHybrid = ({ onTransactionCreated, compact = false, inModal = false, onCancel, onTransferToForm }) => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [voiceUsage, setVoiceUsage] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcription, setTranscription] = useState('');
  const [interimTranscription, setInterimTranscription] = useState('');
  const [classificationResult, setClassificationResult] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [useWhisper, setUseWhisper] = useState(false);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [showItemsAccordion, setShowItemsAccordion] = useState(true);
  const [accountDetectionStatus, setAccountDetectionStatus] = useState(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [newAccount, setNewAccount] = useState({ 
    name: '', 
    type: 'bank', 
    currency: 'MXN', 
    initial_balance: '',
    color: '#3b82f6',
    icon: 'bank'
  });

  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const transcriptionRef = useRef('');
  const interimTranscriptionRef = useRef('');

  // Check if user has access to Voice AI
  const hasVoiceAccess = user?.subscription_tier === 'premium' || user?.subscription_tier === 'pro';

  // Detect Web Speech API support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      setUseWhisper(false);
      initializeWebSpeech();
    } else {
      setUseWhisper(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasVoiceAccess) {
      fetchVoiceUsage();
      fetchCategories();
      fetchAccounts();
      const interval = setInterval(fetchVoiceUsage, 10000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVoiceAccess, token]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
      // Set default account if exists
      const defaultAccount = response.data.find(acc => acc.is_default);
      if (defaultAccount && !selectedAccountId) {
        setSelectedAccountId(defaultAccount.id);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const handleCreateAccount = async () => {
    if (!newAccount.name.trim()) {
      showToast.error('El nombre de la cuenta es requerido');
      return;
    }
    
    setIsProcessing(true);
    try {
      const payload = {
        name: newAccount.name.trim(),
        type: newAccount.type,
        currency: newAccount.currency,
        initial_balance: parseFloat(newAccount.initial_balance) || 0,
        color: newAccount.color,
        icon: newAccount.icon
      };

      if (newAccount.type === 'credit_card') {
        if (newAccount.credit_limit) payload.credit_limit = parseFloat(newAccount.credit_limit) || 0;
        if (newAccount.cut_day) payload.cut_day = parseInt(newAccount.cut_day, 10);
        if (newAccount.payment_due_day) payload.payment_due_day = parseInt(newAccount.payment_due_day, 10);
      }

      const response = await axios.post(
        `${API}/accounts`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const created = response.data;
      showToast.success('Cuenta creada exitosamente');
      setAccounts(prev => [...prev, created]);
      setSelectedAccountId(created.id);
      setAccountDetectionStatus({
        matched: true,
        name: created.name,
        message: `Cuenta asignada: ${created.name}`
      });
      setNewAccount({ 
        name: '', 
        type: 'bank', 
        currency: 'MXN', 
        initial_balance: '',
        credit_limit: '',
        cut_day: '',
        payment_due_day: '',
        color: '#3b82f6', 
        icon: 'bank' 
      });
      setShowNewAccountForm(false);
    } catch (error) {
      console.error('Error al crear cuenta:', error);
      showToast.error(error.response?.data?.detail || 'Error al crear la cuenta');
    } finally {
      setIsProcessing(false);
    }
  };

  const initializeWebSpeech = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.lang = 'es-ES';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interim = '';

      // Android Chrome bug fix: Prevenir duplicación de texto
      // Solo procesar resultados desde el último índice procesado
      const startIndex = event.resultIndex;

      for (let i = startIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interim += transcript;
        }
      }

      if (finalTranscript) {
        setTranscription(prev => {
          const newText = (prev + ' ' + finalTranscript).trim();

          // Deduplicación avanzada para Android
          // Primero eliminar palabras duplicadas consecutivas
          const words = newText.split(' ');
          let deduplicated = [];

          for (let i = 0; i < words.length; i++) {
            // Solo agregar si no es igual a la palabra anterior
            if (i === 0 || words[i].toLowerCase() !== words[i - 1].toLowerCase()) {
              deduplicated.push(words[i]);
            }
          }

          // Luego eliminar patrones de frases duplicadas
          let result = deduplicated.join(' ');

          // Buscar y eliminar patrones repetidos (ej: "gasto de $50 gasto de $50")
          // Dividir en segmentos de 2-5 palabras y buscar duplicados
          for (let segmentSize = 5; segmentSize >= 2; segmentSize--) {
            const segments = result.split(' ');
            let cleaned = [];
            let skip = 0;

            for (let i = 0; i < segments.length; i++) {
              if (skip > 0) {
                skip--;
                continue;
              }

              // Tomar segmento actual
              const currentSegment = segments.slice(i, i + segmentSize).join(' ').toLowerCase();
              const nextSegment = segments.slice(i + segmentSize, i + segmentSize * 2).join(' ').toLowerCase();

              // Si el siguiente segmento es igual, saltar la duplicación
              if (currentSegment === nextSegment && currentSegment.length > 0) {
                cleaned.push(...segments.slice(i, i + segmentSize));
                skip = segmentSize; // Saltar el segmento duplicado
              } else {
                cleaned.push(segments[i]);
              }
            }

            result = cleaned.join(' ');
          }

          const finalResult = result.trim();
          transcriptionRef.current = finalResult;
          return finalResult;
        });
      }
      interimTranscriptionRef.current = interim;
      setInterimTranscription(interim);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        showToast.error('No se detectó voz. Intenta de nuevo.');
      } else if (event.error === 'not-allowed') {
        showToast.error('Permiso de micrófono denegado.');
      }
      setIsRecording(false);
    };

    recognition.onend = () => {
      if (isRecording) {
        try {
          recognition.start();
        } catch (e) {
          setIsRecording(false);
        }
      }
    };

    recognitionRef.current = recognition;
  };

  const fetchVoiceUsage = async () => {
    try {
      const response = await axios.get(`${API}/voice/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoiceUsage(response.data);
    } catch (error) {
      console.error('Error fetching voice usage:', error);
    }
  };

  const handleOpenCreditsDialog = () => {
    navigate('/voice-credits');
  };

  const startRecording = async () => {
    setTranscription('');
    setInterimTranscription('');
    transcriptionRef.current = '';
    interimTranscriptionRef.current = '';
    setRecordingTime(0);

    if (useWhisper) {
      await startWhisperRecording();
    } else {
      startWebSpeechRecording();
    }
  };

  const startWebSpeechRecording = () => {
    try {
      recognitionRef.current.start();
      setIsRecording(true);

      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting Web Speech:', error);
      showToast.error('Error al iniciar grabación');
    }
  };

  const startWhisperRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // Encontrar el mejor MIME type soportado (vital para iOS Safari)
      const types = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/aac'];
      let mimeType = '';
      for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      // Si no encuentra uno específico, pasa un objeto vacío y deja que iOS elija su formato por defecto
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Usar el mimeType real del grabador o caer en mp4 para iOS
        const actualMimeType = mediaRecorder.mimeType || mimeType || 'audio/mp4';
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        
        // Determinar la extensión correcta basada en el mimeType
        let extension = 'webm';
        if (actualMimeType.includes('mp4')) extension = 'mp4';
        else if (actualMimeType.includes('ogg')) extension = 'ogg';
        else if (actualMimeType.includes('aac')) extension = 'aac';
        else if (actualMimeType.includes('mpeg')) extension = 'mp3';

        await processWhisperAudio(audioBlob, `recording.${extension}`);
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(track => track.stop());
          mediaStreamRef.current = null;
        } else {
          stream.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start();
      setIsRecording(true);

      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      showToast.error('No se pudo acceder al micrófono. Verifica los permisos.');
    }
  };

  const stopRecording = () => {
    clearInterval(timerIntervalRef.current);
    setIsRecording(false);

    if (useWhisper) {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
    } else {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      
      // Esperamos medio segundo para permitir que Web Speech dispare su evento 'onresult' final
      setTimeout(() => {
        const finalString = `${transcriptionRef.current} ${interimTranscriptionRef.current}`.trim();
        if (finalString) {
          processTranscription(finalString);
        } else {
          showToast.error('No se detectó ninguna transcripción. Por favor, habla más claro o acércate al micrófono.');
        }
      }, 500);
    }
  };

  const processWhisperAudio = async (audioBlob, filename = 'recording.webm') => {
    setIsProcessing(true);

    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, filename);

      const transcribeResponse = await axios.post(
        `${API}/voice/transcribe-audio`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000 // 60 seconds for audio upload/transcription
        }
      );

      const transcribedText = transcribeResponse.data.transcribed_text;
      setTranscription(transcribedText);
      await processTranscription(transcribedText);
    } catch (error) {
      console.error('Error processing audio:', error);

      // Reset states on error
      setTranscription('');
      setClassificationResult(null);
      setEditedData(null);

      if (error.response?.status === 403) {
        showToast.error('Necesitas una suscripción Premium o Pro para usar Voice AI');
      } else if (error.response?.status === 429) {
        showToast.error(error.response?.data?.detail || 'Has alcanzado tu límite mensual de Voice AI');
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        showToast.error('La transcripción tardó demasiado. Por favor, intenta con un audio más corto.');
      } else {
        showToast.error(error.response?.data?.detail || 'Error al procesar el audio. Intenta de nuevo.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const processTranscription = async (text) => {
    setIsProcessing(true);

    try {
      const classifyResponse = await axios.post(
        `${API}/voice/classify-transaction`,
        { transcribed_text: text },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 35000 // 35 seconds timeout (backend does 2 retries of 15s each)
        }
      );

      if (classifyResponse.data.success) {
        const tx = classifyResponse.data.transaction;
        setClassificationResult(tx);
        setEditedData(tx);
        setShowItemsAccordion(Boolean(tx.items_breakdown && tx.items_breakdown.length > 0));

        // Detección de cuenta bancaria (Punto 34)
        if (tx.detected_account_name && accounts.length > 0) {
          const matchAcc = accounts.find(a => 
            a.name.toLowerCase().includes(tx.detected_account_name.toLowerCase()) ||
            tx.detected_account_name.toLowerCase().includes(a.name.toLowerCase())
          );
          if (matchAcc) {
            setSelectedAccountId(matchAcc.id);
            setAccountDetectionStatus({ 
              matched: true, 
              name: matchAcc.name,
              message: `Cuenta asignada: ${matchAcc.name}`
            });
          } else {
            setAccountDetectionStatus({ 
              matched: false, 
              name: tx.detected_account_name,
              message: `Mencionaste "${tx.detected_account_name}" pero no está en tus cuentas.`
            });
          }
        } else if (tx.detected_account_name) {
          setAccountDetectionStatus({ 
            matched: false, 
            name: tx.detected_account_name,
            message: `Mencionaste "${tx.detected_account_name}" pero no está en tus cuentas.`
          });
        } else {
          setAccountDetectionStatus(null);
        }

        await fetchVoiceUsage();
      } else {
        // API returned error in response
        throw new Error(classifyResponse.data.error_message || 'Error al clasificar');
      }
    } catch (error) {
      console.error('Error classifying transaction:', error);

      // Reset states on error
      setTranscription('');
      setClassificationResult(null);
      setEditedData(null);

      // Show specific error messages
      if (error.response?.status === 503) {
        showToast.error('El servicio de AI no está disponible temporalmente. Por favor, intenta de nuevo en unos momentos.');
      } else if (error.response?.status === 429) {
        showToast.error(error.response?.data?.detail || 'Has alcanzado tu límite mensual de Voice AI');
      } else if (error.response?.status === 403) {
        showToast.error('Necesitas una suscripción Premium o Pro para usar Voice AI');
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        showToast.error('La solicitud tardó demasiado. Por favor, intenta de nuevo.');
      } else {
        showToast.error(error.response?.data?.detail || error.message || 'Error al clasificar la transacción');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmTransaction = async () => {
    if (!editedData) return;

    setIsProcessing(true); // Mostrar loading

    try {
      // Find or create category
      let categoryId = null;

      // Try to find existing category by name
      const existingCategory = categories.find(
        cat => cat.name.toLowerCase() === editedData.category.toLowerCase()
      );

      if (existingCategory) {
        categoryId = existingCategory.id;
      } else {
        // Create new category with default values
        const newCategoryResponse = await axios.post(
          `${API}/categories`,
          {
            name: editedData.category,
            type: editedData.type === 'ingreso' ? 'ingreso' : 'gasto',
            color: '#8B5CF6' // Purple default color
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        categoryId = newCategoryResponse.data.id;
      }

      // Get current date in user's local timezone (browser's timezone)
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const localDate = `${year}-${month}-${day}`;

      // Create transaction with category_id, account_id and items_breakdown
      const transactionData = {
        type: editedData.type,
        amount: parseFloat(editedData.amount),
        description: editedData.description,
        category_id: categoryId,
        account_id: selectedAccountId || null,
        date: localDate,
        items_breakdown: (editedData.items_breakdown && editedData.items_breakdown.length > 0) ? editedData.items_breakdown : null
      };

      await axios.post(`${API}/transactions`, transactionData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showToast.success('¡Transacción creada exitosamente!');

      // Reset all states
      setTranscription('');
      setInterimTranscription('');
      setClassificationResult(null);
      setEditedData(null);
      setIsEditing(false);
      setIsProcessing(false);

      // Refresh categories in case we created a new one
      await fetchCategories();

      if (onTransactionCreated) {
        onTransactionCreated();
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      setIsProcessing(false);
      showToast.error(error.response?.data?.detail || 'Error al crear la transacción');
    }
  };

  const executeCancelTransaction = (notify = true) => {
    // 1. Detener temporizador
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // 2. Liberar micrófono y detener todas las pistas de audio activas
    if (mediaStreamRef.current) {
      try {
        mediaStreamRef.current.getTracks().forEach(track => {
          track.stop();
          track.enabled = false;
        });
      } catch (e) {
        console.warn('Error al detener mediaStream:', e);
      }
      mediaStreamRef.current = null;
    }

    // 3. Detener MediaRecorder si sigue activo
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        console.warn('Error al detener mediaRecorder:', e);
      }
      mediaRecorderRef.current = null;
    }

    // 4. Abortar reconocimiento de voz Web Speech
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch (e) {
        console.warn('Error al abortar WebSpeech:', e);
      }
    }

    // 5. Resetear TODOS los estados a limpio para poder grabar de inmediato
    setIsRecording(false);
    setIsProcessing(false);
    setRecordingTime(0);
    setTranscription('');
    setInterimTranscription('');
    transcriptionRef.current = '';
    interimTranscriptionRef.current = '';
    audioChunksRef.current = [];
    setClassificationResult(null);
    setEditedData(null);
    setIsEditing(false);
    setShowCancelConfirm(false);
    setShowNewAccountForm(false);
    toast.dismiss('confirm-cancel-voice-tx');

    if (notify) {
      showToast.info('Transacción cancelada. Listo para grabar de nuevo.');
    }
    if (onCancel) {
      onCancel();
    }
  };

  const handleCancelTransaction = (force = false) => {
    // Si hay una transacción detectada o transcripción pendiente, confirmar antes de descartar
    if (!force && (classificationResult || (transcription && !isRecording && !isProcessing))) {
      setShowCancelConfirm(true);
      toast.custom(
        (t) => (
          <div className="bg-slate-900/95 text-white p-4 rounded-2xl shadow-2xl border border-slate-700/80 max-w-sm w-full space-y-3 backdrop-blur-md">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white">¿Descartar transacción?</p>
                <p className="text-xs text-slate-300">Se perderán los datos detectados por la IA.</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(t);
                  setShowCancelConfirm(false);
                }}
                className="px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 active:scale-95 transition-all"
              >
                Seguir editando
              </button>
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(t);
                  executeCancelTransaction(true);
                }}
                className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white shadow-sm active:scale-95 transition-all"
              >
                Sí, descartar
              </button>
            </div>
          </div>
        ),
        {
          id: 'confirm-cancel-voice-tx',
          duration: 10000,
        }
      );
      return;
    }
    executeCancelTransaction(true);
  };

  // Diálogo nativo elegante de confirmación
  const renderCancelConfirmDialog = () => {
    if (!showCancelConfirm) return null;

    return (
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
        <div className="bg-white rounded-3xl p-6 max-w-xs w-full text-center space-y-4 shadow-2xl animate-scaleUp">
          <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div className="space-y-1">
            <h4 className="text-base font-black text-slate-900">¿Descartar transacción?</h4>
            <p className="text-xs text-slate-500 font-medium leading-relaxed">Se perderán los datos detectados por la IA.</p>
          </div>
          <div className="flex flex-col gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                toast.dismiss('confirm-cancel-voice-tx');
                executeCancelTransaction(true);
              }}
              className="w-full py-3 bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-bold text-sm rounded-2xl transition-all shadow-md shadow-rose-200"
            >
              Sí, descartar
            </button>
            <button
              type="button"
              onClick={() => {
                toast.dismiss('confirm-cancel-voice-tx');
                setShowCancelConfirm(false);
              }}
              className="w-full py-3 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-700 font-bold text-sm rounded-2xl transition-all"
            >
              Seguir editando
            </button>
          </div>
        </div>
      </div>
    );
  };

  const isBottomSheetOpen = !inModal && Boolean(classificationResult || (transcription && !isRecording && !isProcessing));

  // Bloquear scroll de fondo cuando el Bottom Sheet está abierto
  useEffect(() => {
    if (isBottomSheetOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isBottomSheetOpen]);

  // Limpieza completa al desmontar el componente (liberar micrófono y reconocimiento)
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (mediaStreamRef.current) {
        try {
          mediaStreamRef.current.getTracks().forEach(track => {
            track.stop();
            track.enabled = false;
          });
        } catch (e) {}
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {}
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {}
      }
      toast.dismiss('confirm-cancel-voice-tx');
    };
  }, []);

  // Gestos táctiles para deslizar y cerrar el sheet (Swipe Down)
  const [touchStartY, setTouchStartY] = useState(null);

  const handleTouchStart = (e) => {
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchEnd = (e) => {
    if (touchStartY === null) return;
    const touchEndY = e.changedTouches[0].clientY;
    const distance = touchEndY - touchStartY;
    // Si arrastra más de 60px hacia abajo, pedir confirmación
    if (distance > 60) {
      handleCancelTransaction(false);
    }
    setTouchStartY(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTypeInSpanish = (type) => {
    const types = {
      'expense': 'Gasto',
      'income': 'Ingreso',
      'saving': 'Ahorro'
    };
    return types[type] || type;
  };

  if (!hasVoiceAccess) {
    if (compact) {
      return (
        <button
          onClick={() => navigate('/pricing')}
          className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-2xl active:scale-95 transition-transform"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div className="text-left min-w-0">
            <p className="text-xs font-black text-slate-900">Voice AI</p>
            <p className="text-[10px] text-purple-600 font-semibold">Función Premium — Toca para ver planes</p>
          </div>
        </button>
      );
    }
    return (
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
        <div className="text-center space-y-3">
          <Zap className="w-12 h-12 text-purple-600 mx-auto" />
          <h3 className="text-lg font-bold text-slate-900">Voice AI - Función Premium</h3>
          <p className="text-sm text-slate-600">
            Registra transacciones 10x más rápido usando solo tu voz
          </p>
          <Button
            onClick={() => navigate('/pricing')}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            Ver Planes Premium
          </Button>
        </div>
      </Card>
    );
  }

  // Contenido común del resultado (transcripción + preview/edición)
  const renderResultContent = () => (
    <>
      {/* Transcripción */}
      {transcription && (
        <div className="px-1 pb-1">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Escuchado</p>
          <p className="text-xs text-slate-600 italic leading-snug">"{transcription}"</p>
        </div>
      )}

      {/* Classification Result - Edit Mode */}
      {classificationResult && isEditing && (
        <div className="space-y-3 p-4 bg-slate-50 rounded-3xl border border-slate-100">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-black text-slate-900">Editar Transacción</p>
            <button
              onClick={() => setIsEditing(false)}
              className="w-7 h-7 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center transition-colors"
            >
              <X className="w-3.5 h-3.5 text-slate-600" />
            </button>
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Tipo</label>
            <select
              value={editedData.type}
              onChange={(e) => setEditedData({ ...editedData, type: e.target.value })}
              className="w-full mt-1 p-2.5 text-sm font-semibold border border-slate-200 rounded-2xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="income">Ingreso</option>
              <option value="expense">Gasto</option>
              <option value="saving">Ahorro</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Monto</label>
            <input
              type="text"
              value={editedData.amount ? formatCurrencyInput(editedData.amount) : ''}
              onChange={(e) => {
                const rawValue = parseCurrency(e.target.value);
                setEditedData({ ...editedData, amount: rawValue });
              }}
              placeholder="$0.00"
              className="w-full mt-1 p-2.5 text-sm font-bold border border-slate-200 rounded-2xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Descripción</label>
            <input
              type="text"
              value={editedData.description}
              onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
              className="w-full mt-1 p-2.5 text-sm border border-slate-200 rounded-2xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">Categoría</label>
            {!isCreatingCategory && categories.length > 0 ? (
              <select
                value={editedData.category}
                onChange={(e) => {
                  if (e.target.value === '__nueva__') {
                    setIsCreatingCategory(true);
                  } else {
                    setEditedData({ ...editedData, category: e.target.value });
                  }
                }}
                className="w-full p-2.5 text-sm border border-slate-200 rounded-2xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccionar categoría...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
                {editedData.category && !categories.some(cat => cat.name.toLowerCase() === editedData.category.toLowerCase()) && (
                  <option value={editedData.category}>{editedData.category} (Sugerida)</option>
                )}
                <option value="__nueva__">+ Agregar nueva categoría...</option>
              </select>
            ) : (
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={editedData.category === '__nueva__' ? '' : editedData.category}
                  onChange={(e) => setEditedData({ ...editedData, category: e.target.value })}
                  placeholder="Nombre de la categoría"
                  className="flex-1 p-2.5 text-sm border border-slate-200 rounded-2xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                {categories.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setIsCreatingCategory(false)}
                    className="px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-2xl border border-slate-200 shrink-0"
                  >
                    Volver
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Desglose de Artículos en Modo Edición */}
          {editedData.items_breakdown?.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-200/80">
              <div className="flex justify-between items-center">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  <Package className="w-3 h-3 text-indigo-600" />
                  Artículos Desglosados ({editedData.items_breakdown.length})
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setEditedData({
                      ...editedData,
                      items_breakdown: [
                        ...editedData.items_breakdown,
                        { id: Date.now().toString(), name: '', price: 0 }
                      ]
                    });
                  }}
                  className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5"
                >
                  <Plus className="w-3 h-3" /> Agregar
                </button>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {editedData.items_breakdown.map((it, i) => (
                  <div key={it.id || i} className="flex items-center gap-1.5 bg-white p-1.5 rounded-xl border border-slate-200">
                    <input
                      type="text"
                      placeholder="Producto"
                      value={it.name}
                      onChange={(e) => {
                        const updated = [...editedData.items_breakdown];
                        updated[i] = { ...updated[i], name: e.target.value };
                        setEditedData({ ...editedData, items_breakdown: updated });
                      }}
                      className="flex-1 text-xs p-1.5 bg-slate-50 rounded-lg border-0 focus:ring-1 focus:ring-indigo-500 font-medium text-slate-800"
                    />
                    <div className="relative w-24">
                      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={it.price === 0 ? '' : it.price}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          const updated = [...editedData.items_breakdown];
                          updated[i] = { ...updated[i], price: val };
                          const newTotal = updated.reduce((acc, curr) => acc + (parseFloat(curr.price) || 0), 0);
                          setEditedData({ ...editedData, items_breakdown: updated, amount: newTotal });
                        }}
                        className="w-full text-xs p-1.5 pl-4 bg-slate-50 rounded-lg border-0 focus:ring-1 focus:ring-indigo-500 font-bold text-slate-900 font-mono"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = editedData.items_breakdown.filter((_, idx) => idx !== i);
                        const newTotal = updated.reduce((acc, curr) => acc + (parseFloat(curr.price) || 0), 0);
                        setEditedData({ ...editedData, items_breakdown: updated, amount: updated.length > 0 ? newTotal : editedData.amount });
                      }}
                      className="p-1 text-slate-400 hover:text-rose-500 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={() => setIsEditing(false)}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-2xl font-black"
          >
            <Check className="w-4 h-4 mr-2" />
            Guardar Cambios
          </Button>
        </div>
      )}

      {/* Classification Result - View Mode */}
      {classificationResult && !isEditing && (
        <div className="space-y-3">
          {/* Chip de tipo */}
          {(() => {
            const typeConfig = {
              income:  { label: 'Ingreso',  bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
              expense: { label: 'Gasto',    bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200',    dot: 'bg-rose-500' },
              saving:  { label: 'Ahorro',   bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    dot: 'bg-blue-500' },
            };
            const t = typeConfig[editedData?.type] || typeConfig.expense;
            return (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-2xl border ${t.bg} ${t.border}`}>
                <div className={`w-2 h-2 rounded-full ${t.dot}`} />
                <span className={`text-xs font-black uppercase tracking-wider ${t.text}`}>{t.label} Detectado</span>
                <CheckCircle2 className={`w-4 h-4 ml-auto ${t.text}`} />
              </div>
            );
          })()}

          {/* Monto grande */}
          <div className="text-center py-2">
            <p className="text-4xl font-black text-slate-900 tracking-tight">
              ${Number(editedData?.amount || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
            </p>
            <p className="text-sm font-semibold text-slate-500 mt-0.5">{editedData?.description}</p>
          </div>

          {/* Detalles */}
          <div className="bg-slate-50 rounded-2xl border border-slate-100 divide-y divide-slate-100">
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Categoría</span>
              <span className="text-xs font-black text-slate-900">{editedData?.category || editedData?.type || 'General'}</span>
            </div>
            <div className="px-4 py-2.5">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Cuenta</span>
              {!showNewAccountForm ? (
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                  {/* Chip: Sin cuenta */}
                  <button
                    type="button"
                    onClick={() => setSelectedAccountId('')}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                      selectedAccountId === ''
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    Sin cuenta
                  </button>
                  {/* Chips de cuentas */}
                  {accounts.map(account => (
                    <button
                      key={account.id}
                      type="button"
                      onClick={() => setSelectedAccountId(account.id)}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                        selectedAccountId === account.id
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      {account.name}
                    </button>
                  ))}
                  {/* Chip: + Nueva */}
                  <button
                    type="button"
                    onClick={() => setShowNewAccountForm(true)}
                    className="shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border border-dashed border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-all"
                  >
                    + Nueva
                  </button>
                </div>
              ) : (
                <div className="space-y-2 p-3 border border-slate-200 rounded-2xl bg-white">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-black text-slate-700">Nueva Cuenta</span>
                    <button type="button" onClick={() => setShowNewAccountForm(false)} className="w-6 h-6 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
                      <X className="w-3.5 h-3.5 text-slate-500" />
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Nombre (ej. BBVA)"
                    value={newAccount.name}
                    onChange={e => setNewAccount({...newAccount, name: e.target.value})}
                    className="w-full p-2.5 text-sm border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <select
                      value={newAccount.type}
                      onChange={e => setNewAccount({...newAccount, type: e.target.value})}
                      className="flex-1 p-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none font-medium"
                    >
                      <option value="bank">Cuenta Bancaria / Débito</option>
                      <option value="credit_card">Tarjeta de Crédito</option>
                      <option value="cash">Efectivo</option>
                      <option value="digital_wallet">Billetera Digital</option>
                      <option value="investment">Inversión</option>
                    </select>
                    <select
                      value={newAccount.currency}
                      onChange={e => setNewAccount({...newAccount, currency: e.target.value})}
                      className="w-24 p-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none font-medium"
                    >
                      <option value="MXN">MXN</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input
                      type="text"
                      placeholder={newAccount.type === 'credit_card' ? "Deuda actual (opcional)" : "Saldo inicial"}
                      value={newAccount.initial_balance || ''}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                        setNewAccount({...newAccount, initial_balance: numericValue});
                      }}
                      className="w-full pl-7 p-2.5 text-sm border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {newAccount.type === 'credit_card' && (
                    <div className="space-y-2 p-2 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                        <input
                          type="text"
                          placeholder="Límite de crédito (opcional)"
                          value={newAccount.credit_limit || ''}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                            setNewAccount({...newAccount, credit_limit: numericValue});
                          }}
                          className="w-full pl-7 p-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Día de corte</label>
                          <input
                            type="number"
                            min="1"
                            max="31"
                            placeholder="Ej. 15"
                            value={newAccount.cut_day || ''}
                            onChange={(e) => setNewAccount({...newAccount, cut_day: e.target.value})}
                            className="w-full p-1.5 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Día límite pago</label>
                          <input
                            type="number"
                            min="1"
                            max="31"
                            placeholder="Ej. 5"
                            value={newAccount.payment_due_day || ''}
                            onChange={(e) => setNewAccount({...newAccount, payment_due_day: e.target.value})}
                            className="w-full p-1.5 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={newAccount.color}
                      onChange={e => setNewAccount({...newAccount, color: e.target.value})}
                      className="w-10 h-10 p-0.5 border border-slate-200 rounded-xl cursor-pointer"
                    />
                    <Button
                      type="button"
                      onClick={handleCreateAccount}
                      disabled={isProcessing}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-2xl font-black"
                    >
                      {isProcessing ? 'Creando...' : 'Crear Cuenta'}
                    </Button>
                  </div>
                </div>
              )}
              {accountDetectionStatus && (
                <div className={`mt-2 p-2.5 rounded-2xl text-xs flex flex-wrap items-center justify-between gap-2 ${
                  accountDetectionStatus.matched 
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                    : 'bg-amber-50 text-amber-900 border border-amber-200'
                }`}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-bold text-sm shrink-0">
                      {accountDetectionStatus.matched ? '✓' : '⚠️'}
                    </span>
                    <span className="font-medium leading-tight">
                      {accountDetectionStatus.message}
                    </span>
                  </div>
                  {!accountDetectionStatus.matched && (
                    <button
                      type="button"
                      onClick={() => {
                        const isCard = /tarjeta|crédito|credito|debito|débito/i.test(accountDetectionStatus.name);
                        setNewAccount({
                          name: accountDetectionStatus.name,
                          type: isCard ? 'credit_card' : 'bank',
                          currency: 'MXN',
                          initial_balance: '',
                          color: '#4f46e5',
                          icon: isCard ? 'credit_card' : 'bank'
                        });
                        setShowNewAccountForm(true);
                      }}
                      className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold text-[11px] shadow-sm transition-all flex items-center gap-1 shrink-0"
                    >
                      <Plus className="w-3 h-3" /> Crear {accountDetectionStatus.name}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Desglose de Artículos (Acordeón en vista previa) */}
          {editedData?.items_breakdown && editedData.items_breakdown.length > 0 && (
            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowItemsAccordion(!showItemsAccordion)}
                className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-slate-100/60 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-black text-slate-700 uppercase tracking-wider">
                    Desglose de Artículos ({editedData.items_breakdown.length})
                  </span>
                </div>
                {showItemsAccordion ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </button>
              {showItemsAccordion && (
                <div className="px-3 pb-3 space-y-1.5 border-t border-slate-200/60 pt-2">
                  {editedData.items_breakdown.map((item, idx) => {
                    const priceVal = parseFloat(item.price ?? item.amount ?? 0);
                    return (
                      <div key={item.id || idx} className="flex justify-between items-center text-xs py-2 px-3 rounded-xl bg-white border border-slate-200/80 shadow-2xs">
                        <span className="text-slate-800 font-semibold truncate flex-1 mr-2">{item.name || `Artículo ${idx + 1}`}</span>
                        <span className="text-emerald-700 dark:text-emerald-600 font-black font-mono shrink-0 text-xs sm:text-sm">
                          ${!isNaN(priceVal) ? priceVal.toFixed(2) : '0.00'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  // Acciones (Confirmar, Editar, Formulario, Cancelar)
  const renderResultActions = (isSticky = false) => (
    <div className={`flex flex-col gap-2.5 ${
      isSticky 
        ? 'sticky bottom-0 bg-white/95 backdrop-blur-md pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] -mx-4 px-4 sm:-mx-6 sm:px-6 border-t border-slate-100 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-20' 
        : ''
    }`}>
      <Button
        onClick={handleConfirmTransaction}
        disabled={isProcessing || showNewAccountForm}
        className="w-full !h-auto min-h-[48px] py-3.5 px-4 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-2xl font-black text-base shadow-md shadow-emerald-100 disabled:opacity-50 flex items-center justify-center cursor-pointer active:scale-[0.99] transition-all"
      >
        {isProcessing ? (
          <><div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />Guardando...</>
        ) : (
          <><Check className="w-5 h-5 mr-2 shrink-0" /><span className="truncate">Confirmar y Guardar</span></>
        )}
      </Button>
      <div className="flex items-center gap-2 w-full">
        <Button
          onClick={() => setIsEditing(true)}
          variant="outline"
          disabled={isProcessing || showNewAccountForm}
          className="flex-1 !h-auto min-h-[42px] py-2.5 px-3 rounded-2xl border-slate-200 font-bold disabled:opacity-50 text-xs sm:text-sm text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1.5"
        >
          <Edit2 className="w-4 h-4 shrink-0 text-slate-500" />Editar
        </Button>
        {onTransferToForm && (
          <Button
            onClick={() => {
              onTransferToForm({
                amount: editedData?.amount,
                description: editedData?.description,
                category: editedData?.category,
                category_id: editedData?.category_id,
                type: editedData?.type,
                account_id: selectedAccountId,
                items_breakdown: editedData?.items_breakdown
              });
              executeCancelTransaction(false);
            }}
            variant="outline"
            disabled={isProcessing || showNewAccountForm}
            className="flex-1 !h-auto min-h-[42px] py-2.5 px-2 rounded-2xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 font-bold disabled:opacity-50 text-xs sm:text-sm flex items-center justify-center gap-1"
            title="Pasar datos al formulario completo"
          >
            <ExternalLink className="w-3.5 h-3.5 shrink-0" />A Formulario
          </Button>
        )}
        <Button
          onClick={() => handleCancelTransaction(false)}
          variant="outline"
          disabled={isProcessing || showNewAccountForm}
          className="flex-1 !h-auto min-h-[42px] py-2.5 px-3 rounded-2xl border-rose-200 text-rose-600 hover:bg-rose-50 font-bold disabled:opacity-50 text-xs sm:text-sm flex items-center justify-center gap-1.5"
        >
          <X className="w-4 h-4 shrink-0 text-rose-500" />Cancelar
        </Button>
      </div>
    </div>
  );

  const renderResultBody = (stickyActions = false) => (
    <div className="space-y-4">
      <div className="space-y-3.5">
        {renderResultContent()}
      </div>
      {renderResultActions(stickyActions)}
    </div>
  );

  // Renderizador del Bottom Sheet de resultado de IA - Totalmente responsivo en móvil y PWA
  const renderBottomSheet = () => {
    if (!classificationResult && !editedData) {
      return null;
    }

    return (
      <>
        {/* Backdrop con confirmación al tocar afuera */}
        <div
          className="fixed inset-0 bg-black/60 z-[110] animate-fadeIn backdrop-blur-[2px]"
          onClick={() => handleCancelTransaction(false)}
        />
        {/* Sheet flotante con soporte para swipe down */}
        <div 
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className="fixed bottom-0 left-0 right-0 z-[120] max-w-lg mx-auto bg-white rounded-t-[2rem] shadow-2xl animate-slide-up max-h-[92vh] flex flex-col border-t border-slate-100 overflow-hidden"
        >
          {/* Header fijo superior con handle y botón cerrar */}
          <div className="shrink-0 px-4 pt-3 pb-2 flex items-center justify-between border-b border-slate-100 bg-white select-none">
            <div className="w-8" />
            <div className="w-12 h-1.5 bg-slate-300 rounded-full cursor-grab active:cursor-grabbing" />
            <button
              type="button"
              onClick={() => handleCancelTransaction(false)}
              className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors"
              title="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Cuerpo scrolleable del resultado */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-3 space-y-3.5 pb-4">
            {renderResultContent()}
          </div>

          {/* Footer fijo inferior con botones de acción siempre visibles y safe area */}
          <div className="shrink-0 px-4 sm:px-6 pt-2.5 pb-[max(1.5rem,env(safe-area-inset-bottom))] border-t border-slate-100 bg-white/95 backdrop-blur-xs space-y-2 shadow-xs">
            {renderResultActions(false)}
          </div>
        </div>
      </>
    );
  };

  // --- MODO INLINE DENTRO DE MODAL (TransactionModal) ---
  if (inModal) {
    if (classificationResult || (transcription && !isRecording && !isProcessing)) {
      return (
        <div className="space-y-4 py-1 animate-fadeIn pb-1">
          {renderResultBody(true)}
          {renderCancelConfirmDialog()}
        </div>
      );
    }

    return (
      <div className="space-y-4 py-2">
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-600" />
            <h3 className="text-sm font-bold text-slate-900">Voice AI</h3>
          </div>
          {voiceUsage && (
            <span className="text-xs font-bold text-purple-600">
              {voiceUsage.remaining} créditos
            </span>
          )}
        </div>

        {/* Recording Button */}
        {!isProcessing && (
          <div className="flex flex-col items-center py-4 space-y-3">
            <Button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isProcessing || voiceUsage?.remaining <= 0}
              className={`w-20 h-20 rounded-full transition-all ${
                isRecording
                  ? 'bg-red-600 hover:bg-red-700 animate-pulse'
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
              }`}
            >
              {isRecording ? <Square className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
            </Button>

            {isRecording && (
              <div className="text-center w-full">
                <p className="text-xl font-bold text-red-600">{formatTime(recordingTime)}</p>
                <p className="text-xs text-slate-500">Grabando... Toca para detener</p>
                {!useWhisper && (transcription || interimTranscription) && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-xl max-w-sm mx-auto">
                    <p className="text-xs text-slate-800">
                      {transcription}<span className="text-slate-400 italic">{interimTranscription}</span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {!isRecording && (
              <div className="text-center space-y-1 px-2">
                <p className="text-xs font-semibold text-slate-600">
                  Toca para comenzar a grabar tu transacción
                </p>
                <p className="text-[11px] text-slate-500">
                  <span className="font-bold text-indigo-600">Prueba lista:</span> "Leche en 32, café en 85 y fruta en 60 con Nu"
                </p>
              </div>
            )}
          </div>
        )}

        {isProcessing && (
          <div className="flex flex-col items-center py-6 space-y-2">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            <p className="text-xs font-medium text-slate-600">
              {useWhisper ? 'Procesando audio...' : 'Clasificando con IA...'}
            </p>
          </div>
        )}
        {renderCancelConfirmDialog()}
      </div>
    );
  }

  // --- MODO COMPACT: fila horizontal fija en dashboard + bottom sheet flotante ---
  if (compact) {
    const canRecord = voiceUsage?.remaining > 0;
    return (
      <>
        <button
          onClick={canRecord ? (isRecording ? stopRecording : startRecording) : undefined}
          disabled={!canRecord && !isRecording}
          className={`w-full flex items-center gap-3 p-3 border rounded-2xl active:scale-95 transition-all ${
            isRecording
              ? 'bg-red-50 border-red-200 animate-pulse'
              : 'bg-white border-slate-100 shadow-sm'
          }`}
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all ${
            isRecording
              ? 'bg-red-600 shadow-lg shadow-red-200'
              : isProcessing
              ? 'bg-purple-100'
              : 'bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-200'
          }`}>
            {isProcessing ? (
              <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
            ) : isRecording ? (
              <Square className="w-5 h-5 text-white" />
            ) : (
              <Mic className="w-5 h-5 text-white" />
            )}
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className="text-xs font-black text-slate-900">
              {isRecording ? `Grabando... ${formatTime(recordingTime)}` : isProcessing ? 'Procesando con IA...' : 'Voice AI'}
            </p>
            <p className="text-[10px] text-slate-400 font-medium truncate">
              {isRecording ? 'Toca para detener' : isProcessing ? 'Clasificando tu gasto...' : 'Toca para grabar una transacción'}
            </p>
            {!isRecording && !isProcessing && (
              <p className="text-[10px] text-purple-600 font-medium truncate mt-0.5">
                "Leche 32, café 85 con Santander" ó "Comida 250"
              </p>
            )}
            {/* Transcripción en tiempo real (Web Speech) */}
            {isRecording && !useWhisper && (transcription || interimTranscription) && (
              <p className="text-[10px] text-blue-600 font-medium mt-0.5 truncate">
                {transcription}{interimTranscription}
              </p>
            )}
          </div>
          {voiceUsage && (
            <div className="text-right shrink-0">
              <p className="text-[10px] text-slate-400">Créditos</p>
              <p className="text-xs font-black text-purple-600">{voiceUsage.remaining}</p>
            </div>
          )}
        </button>

        {/* Modal Bottom Sheet Flotante */}
        {renderBottomSheet()}
        {renderCancelConfirmDialog()}
      </>
    );
  }

  return (
    <>
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-purple-600" />
            <h3 className="text-lg font-bold text-slate-900">Voice AI</h3>
          </div>

          {voiceUsage && (
            <div className="text-right">
              <p className="text-xs text-slate-500">Créditos Disponibles</p>
              <p className="text-sm font-bold text-purple-600">
                {voiceUsage.remaining} de {voiceUsage.monthly_limit + voiceUsage.purchased_credits}
              </p>
            </div>
          )}
        </div>

        {/* Low credits warning */}
        {voiceUsage && voiceUsage.remaining > 0 && voiceUsage.remaining <= 10 && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-yellow-800">
                  Quedan pocos créditos ({voiceUsage.remaining})
                </p>
                <Button
                  onClick={handleOpenCreditsDialog}
                  size="sm"
                  className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  Comprar más créditos
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* No credits remaining */}
        {voiceUsage && voiceUsage.remaining === 0 && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-red-800 mb-1">
                  Has alcanzado el límite mensual
                </p>
                <p className="text-xs text-red-700 mb-3">
                  Compra créditos adicionales para seguir usando Voice AI este mes
                </p>
                <Button
                  onClick={handleOpenCreditsDialog}
                  size="sm"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  Ver paquetes de créditos
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Recording Button */}
        {!classificationResult && !isProcessing && voiceUsage?.remaining > 0 && (
          <div className="flex flex-col items-center py-6 space-y-3">
            <Button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isProcessing}
              className={`w-24 h-24 rounded-full transition-all ${
                isRecording
                  ? 'bg-red-600 hover:bg-red-700 animate-pulse'
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
              }`}
            >
              {isRecording ? (
                <Square className="w-10 h-10" />
              ) : (
                <Mic className="w-10 h-10" />
              )}
            </Button>

            {isRecording && (
              <div className="text-center w-full">
                <p className="text-2xl font-bold text-red-600">{formatTime(recordingTime)}</p>
                <p className="text-sm text-slate-600 mb-2">Grabando... Click para detener</p>

                {/* Transcripción en tiempo real (solo Web Speech) */}
                {!useWhisper && (transcription || interimTranscription) && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg max-w-md mx-auto">
                    <p className="text-xs font-semibold text-blue-700 mb-1">Transcribiendo:</p>
                    <p className="text-sm text-slate-800 text-left">
                      {transcription}
                      <span className="text-slate-500 italic">{interimTranscription}</span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {!isRecording && (
              <div className="text-center space-y-2 max-w-sm mx-auto px-2">
                <p className="text-sm font-semibold text-slate-700 hidden sm:block">
                  Click para comenzar a grabar tu transacción
                </p>
                <div className="text-xs text-slate-500 space-y-1 bg-slate-50/80 p-2.5 rounded-2xl border border-slate-100 hidden sm:block">
                  <p className="font-semibold text-slate-600">
                    <span className="font-bold text-purple-600">📋 Lista / Desglose:</span> "Compré leche en 32, café en 85 y cereal en 60 con tarjeta Santander"
                  </p>
                  <p className="text-[11px] text-slate-400">
                    <span className="font-bold text-slate-500">Simple:</span> "Gasté 250 en comida" • "Ingreso de 5000 por trabajo"
                  </p>
                </div>

                {/* Versión móvil ultracompacta sin iconos para no ocupar espacio */}
                <div className="sm:hidden text-center py-1">
                  <p className="text-[11px] text-slate-500 font-medium leading-tight">
                    "Leche 32, café 85 con Santander" ó "Comida 250"
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Processing - inline en card */}
        {isProcessing && (
          <div className="flex flex-col items-center py-4 space-y-2">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            <p className="text-xs font-medium text-slate-600">
              {useWhisper ? 'Procesando audio...' : 'Clasificando transacción...'}
            </p>
          </div>
        )}
      </Card>

      {/* Modal Bottom Sheet Flotante para Desktop */}
      {renderBottomSheet()}
      {renderCancelConfirmDialog()}
    </>
  );
};

export default VoiceRecorderHybrid;
